import { useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UI, DISPLAY } from '../styles/tl5';
import { ROUTES, buildRoute } from '../constants/routes';
import { useSubmitCorporateEnquiryMutation, useGetContactInfoQuery } from '../store/siteContentApi';
import {
  PACKAGE_TABS, PROGRAMMES, HOW_IT_WORKS, CORPORATE_FAQS, NEEDS, TEAM_SIZES, CREDENTIALS,
} from '../data/corporatePackages';
import heroImg from '../assets/img/v5/corporate-hero.jpg';

// Corporate — "TrustLab for Business", rebuilt to the v5 reference.
// This page existed against the previous reference; the structure below
// is the new one throughout.
//
// Three places where ours and the reference part company:
//
//   · The enquiry form. The reference asks for company, name, email,
//     phone and cities. `POST /corporate/enquiry` additionally requires
//     **industry**, **designation** and **consent**, and 400s without
//     them — so those three are on the form. The corporate team needs
//     the first two to qualify a lead anyway.
//   · The package cards are real. All twelve of the reference's package
//     names exist in our catalogue, so each card links to its own
//     package page and carries a parameter count counted from the real
//     components list (see data/corporatePackages.js).
//   · The HR dashboard panel keeps its shape but carries no numbers.
//     The reference fills it with `[%]`, `[n]` and invented trends —
//     "62% Low Vitamin D", "38% High cholesterol". Those read as
//     TrustLab's own findings about a real workforce. We have no
//     corporate analytics to draw on (API-NOTES 2.41), so the panel
//     shows the metrics it would report and says the numbers are the
//     client's own once the programme runs.

const FALLBACK = { phone: '18005995656', whatsapp: '+919014638633', email: 'admin@mytrustlab.com' };

const PRIVACY_ROWS = [
  ['Employee', 'Full report on WhatsApp and in the app, plus a free call with a lab doctor if anything is flagged.'],
  ['HR', 'Participation, booking status and anonymised health trends. No individual results without the employee’s consent.'],
];

const DASHBOARD_TILES = ['Employees tested', 'Booked, not yet collected', 'Doctor calls completed'];
const DASHBOARD_TRENDS = ['Low Vitamin D', 'High cholesterol', 'Pre-diabetic HbA1c', 'Low haemoglobin'];

const EMPTY = {
  company: '', industry: '', contact_name: '', designation: '',
  email: '', phone: '', locations: '',
};

// ── shared fragments ──────────────────────────────────────────────────
const eyebrow = {
  fontFamily: UI,
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: 'var(--tl-teal-d)',
};

const h2 = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 'clamp(30px, 3.8vw, 48px)',
  lineHeight: 1,
  letterSpacing: '-.03em',
  textWrap: 'balance',
  color: 'var(--tl-g9)',
};

const field = {
  border: '1.5px solid var(--tl-line)',
  borderRadius: '14px',
  outline: 0,
  font: 'inherit',
  fontFamily: UI,
  fontSize: 16,
  p: '13px 14px',
  color: 'var(--tl-g9)',
  bgcolor: '#FFFFFF',
  width: '100%',
  minWidth: 0,
  '&:focus': { borderColor: 'var(--tl-teal)' },
};

const fieldLabel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  minWidth: 0,
  fontFamily: UI,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--tl-muted-2)',
};

const chipSx = (on) => ({
  cursor: 'pointer',
  borderRadius: '999px',
  p: '10px 16px',
  whiteSpace: 'nowrap',
  font: 'inherit',
  fontFamily: UI,
  fontSize: 14,
  fontWeight: 700,
  border: `1.5px solid ${on ? 'var(--tl-g9)' : 'var(--tl-line)'}`,
  bgcolor: on ? 'var(--tl-g9)' : '#FFFFFF',
  color: on ? '#FFFFFF' : 'var(--tl-g9)',
});

const Corporate = () => {
  const [submit, { isLoading: sending }] = useSubmitCorporateEnquiryMutation();
  const { data: contact } = useGetContactInfoQuery();

  const [tab, setTab] = useState(0);
  const [faq, setFaq] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [size, setSize] = useState(1);
  const [needs, setNeeds] = useState(['AHC']);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const phone = String(contact?.phone || FALLBACK.phone).replace(/\D/g, '');
  const whatsapp = String(contact?.whatsapp || FALLBACK.whatsapp).replace(/\D/g, '');
  const email = contact?.email || FALLBACK.email;
  const phoneLabel = phone.replace(/(\d{4})(\d{3})(\d{4})/, '$1 $2 $3');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleNeed = (code) =>
    setNeeds((cur) => (cur.includes(code) ? cur.filter((x) => x !== code) : [...cur, code]));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.company.trim()) return setError('Please enter your company name');
    if (!form.industry.trim()) return setError('Please enter your industry');
    if (!form.contact_name.trim()) return setError('Please enter your name');
    if (!form.designation.trim()) return setError('Please enter your designation');
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return setError('Please enter a valid work email');
    if (form.phone.replace(/\D/g, '').length < 10) return setError('Please enter a 10-digit phone number');
    if (!consent) return setError('Please tick the consent box so we can contact you');
    setError('');

    try {
      await submit({
        ...form,
        company: form.company.trim(),
        industry: form.industry.trim(),
        contact_name: form.contact_name.trim(),
        designation: form.designation.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        locations: form.locations.trim(),
        employees: TEAM_SIZES[size],
        programs: needs,
        // The admin stores codes; keep the labels the prospect actually
        // ticked so nothing is lost if the mapping is ever reworded.
        notes: `Programmes requested: ${NEEDS.filter((n) => needs.includes(n.code)).map((n) => n.label).join(', ') || 'none ticked'}`,
        consent: true,
        source_page: window.location.href,
      }).unwrap();
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      setError(err?.data?.message || 'Could not send your enquiry — please try again.');
    }
    return undefined;
  };

  return (
    <Box>
      {/* ── 1. hero ──────────────────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 2, px: 2 }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 1320,
            mx: 'auto',
            minHeight: { xs: 380, md: 520 },
            display: 'flex',
            alignItems: 'end',
            borderRadius: '36px',
            overflow: 'hidden',
            bgcolor: 'var(--tl-g9)',
          }}
        >
          <Box component="img" src={heroImg} alt="" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, rgba(7,33,29,0.94) 0%, rgba(7,33,29,0.76) 52%, rgba(7,33,29,0.25) 100%)',
            }}
          />
          <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 860, width: '100%', p: 'clamp(28px, 5vw, 56px)', color: '#FFFFFF' }}>
            <Box
              component="span"
              sx={{ alignSelf: 'start', p: '7px 12px', borderRadius: '999px', bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)', fontFamily: UI, fontSize: 13, fontWeight: 800 }}
            >
              TrustLab for Business
            </Box>
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(34px, 5.8vw, 78px)',
                lineHeight: 0.95,
                letterSpacing: '-.035em',
                textWrap: 'balance',
                color: '#FFFFFF',
              }}
            >
              Healthier teams.{' '}
              <Box component="span" sx={{ color: 'var(--tl-teal-l)' }}>One partner, end to end.</Box>
            </Typography>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.55, color: '#D5E7E2', maxWidth: 600 }}>
              Annual health checks, on-site camps and pre-employment screening, tested in our
              NABL-accredited lab. Employees get private, doctor-verified reports. HR gets one
              dashboard and anonymised insights.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
              <Box
                component="a"
                href="#enquire"
                sx={{ p: '15px 22px', borderRadius: '999px', bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)', fontFamily: UI, fontSize: 16, fontWeight: 800, textDecoration: 'none' }}
              >
                Request a proposal
              </Box>
              <Box
                component="a"
                href={`tel:${phone}`}
                sx={{ p: '14px 22px', borderRadius: '999px', border: '1.5px solid #5A7D75', color: '#FFFFFF', fontFamily: UI, fontSize: 16, fontWeight: 800, textDecoration: 'none' }}
              >
                Call {phoneLabel}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box component="main" sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9, display: 'flex', flexDirection: 'column' }}>

        {/* ── 2. credentials ─────────────────────────────────────────── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 1.5 }}>
          {CREDENTIALS.map(([v, l]) => (
            <Box key={l} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 3, borderRadius: '28px', bgcolor: '#FFFFFF' }}>
              <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 40, lineHeight: 1, color: 'var(--tl-teal-d)' }}>{v}</Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-muted-2)' }}>{l}</Box>
            </Box>
          ))}
        </Box>

        {/* ── 3. programmes ──────────────────────────────────────────── */}
        <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, mt: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, px: 1, maxWidth: 760 }}>
            <Box component="span" sx={eyebrow}>What we run</Box>
            <Typography component="h2" sx={{ ...h2, fontSize: 'clamp(32px, 4vw, 52px)' }}>
              Four programmes, one lab behind them.
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 1.5 }}>
            {PROGRAMMES.map((p) => (
              <Box
                key={p.t}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, minHeight: 280, p: 3.5, borderRadius: '32px', bgcolor: p.bg, color: p.fg }}
              >
                <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 15, color: p.num }}>{p.n}</Box>
                <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 28, lineHeight: 1.05, letterSpacing: '-.02em' }}>{p.t}</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: p.body }}>{p.d}</Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── 4. packages ────────────────────────────────────────────── */}
        <Box
          component="section"
          sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 6, p: 'clamp(24px, 4vw, 48px)', borderRadius: '36px', bgcolor: '#FFFFFF' }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, maxWidth: 640 }}>
              <Box component="span" sx={eyebrow}>Packages for teams</Box>
              <Typography component="h2" sx={h2}>Start from our library. Adjust for your workforce.</Typography>
            </Box>
            <Box component={Link} to={ROUTES.PACKAGES} sx={{ fontFamily: UI, fontWeight: 800, fontSize: 15, color: 'var(--tl-teal-d)', textDecoration: 'none' }}>
              See all packages →
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, alignSelf: 'start', maxWidth: '100%', overflowX: 'auto', p: 0.75, borderRadius: '999px', bgcolor: 'var(--tl-mint)', scrollbarWidth: 'none' }}>
            {PACKAGE_TABS.map((t, i) => {
              const on = i === tab;
              return (
                <Box
                  key={t.key}
                  component="button"
                  type="button"
                  onClick={() => setTab(i)}
                  aria-pressed={on}
                  sx={{
                    border: 0,
                    cursor: 'pointer',
                    borderRadius: '999px',
                    p: '11px 18px',
                    whiteSpace: 'nowrap',
                    font: 'inherit',
                    fontFamily: UI,
                    fontSize: 15,
                    fontWeight: 800,
                    bgcolor: on ? 'var(--tl-g9)' : 'transparent',
                    color: on ? '#FFFFFF' : 'var(--tl-muted-2)',
                  }}
                >
                  {t.label}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: 1.5 }}>
            {PACKAGE_TABS[tab].items.map((p) => (
              <Box
                key={p.id}
                component={Link}
                to={buildRoute(ROUTES.PACKAGE_DETAIL, { id: p.id })}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                  p: 2.75,
                  borderRadius: '24px',
                  border: '1.5px solid var(--tl-line)',
                  color: 'var(--tl-g9)',
                  textDecoration: 'none',
                  transition: 'border-color .2s var(--tl-ease)',
                  '&:hover': { borderColor: 'var(--tl-teal)' },
                }}
              >
                <Box
                  component="span"
                  sx={{ alignSelf: 'start', p: '6px 10px', borderRadius: '999px', bgcolor: 'var(--tl-mint)', color: 'var(--tl-teal-d)', fontFamily: UI, fontSize: 12, fontWeight: 800 }}
                >
                  {p.for}
                </Box>
                <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, lineHeight: 1.1, letterSpacing: '-.01em' }}>{p.name}</Box>
                <Box component="span" sx={{ flex: 1, fontFamily: UI, fontSize: 14, lineHeight: 1.55, color: 'var(--tl-muted)' }}>{p.q}</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 800, color: 'var(--tl-g9)' }}>{p.params} parameters</Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── 5. privacy + HR dashboard ──────────────────────────────── */}
        <Box component="section" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 6 }}>
          <Box sx={{ flex: '1 1 380px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.25, p: 'clamp(24px, 4vw, 48px)', borderRadius: '36px', bgcolor: 'var(--tl-g9)', color: '#FFFFFF' }}>
            <Box component="span" sx={{ ...eyebrow, color: 'var(--tl-teal-l)' }}>Privacy first</Box>
            <Typography component="h2" sx={{ ...h2, fontSize: 'clamp(30px, 3.6vw, 44px)', color: '#FFFFFF' }}>
              Employees see their results. HR sees the trends.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {PRIVACY_ROWS.map(([k, v]) => (
                <Box key={k} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px minmax(0, 1fr)' }, gap: 1.75, py: 1.75, borderBottom: '1px solid var(--tl-g6)' }}>
                  <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, color: 'var(--tl-teal-l)' }}>{k}</Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.55, color: '#E4F1ED' }}>{v}</Box>
                </Box>
              ))}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px minmax(0, 1fr)' }, gap: 1.75, py: 1.75 }}>
                <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, color: 'var(--tl-teal-l)' }}>Legal</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.55, color: '#E4F1ED' }}>
                  Data handled under the Digital Personal Data Protection Act, 2023.{' '}
                  <Box component={Link} to={`${ROUTES.LEGAL}#privacy`} sx={{ color: 'var(--tl-yellow)', fontWeight: 700 }}>
                    Read our privacy policy
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: '1.2 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.25, p: 'clamp(22px, 3.4vw, 36px)', borderRadius: '36px', bgcolor: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, color: 'var(--tl-g9)' }}>HR dashboard</Box>
              <Box component="span" sx={{ p: '6px 10px', borderRadius: '8px', bgcolor: '#FFF4C2', color: '#8A5A00', fontFamily: UI, fontSize: 12, fontWeight: 800 }}>
                Sample view
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 120px), 1fr))', gap: 1 }}>
              {DASHBOARD_TILES.map((l) => (
                <Box key={l} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1.75, borderRadius: '18px', bgcolor: 'var(--tl-mint)' }}>
                  <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 28, lineHeight: 1, color: 'var(--tl-line-2)' }}>—</Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 12, fontWeight: 800, color: 'var(--tl-muted-2)' }}>{l}</Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 800, color: 'var(--tl-muted-2)' }}>
                Anonymised trends across your team
              </Box>
              {DASHBOARD_TRENDS.map((l) => (
                <Box key={l} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 150px) minmax(0, 1fr)', gap: 1.5, alignItems: 'center' }}>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-g9)' }}>{l}</Box>
                  <Box component="span" sx={{ height: 10, borderRadius: '999px', bgcolor: 'var(--tl-mint)', display: 'block' }} />
                </Box>
              ))}
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 13, lineHeight: 1.5, color: 'var(--tl-muted-2)' }}>
              These are the measures the dashboard reports. The numbers are your team&rsquo;s, once
              the programme runs — and small groups are never shown, so no one can be identified.
            </Box>
          </Box>
        </Box>

        {/* ── 6. how it works ────────────────────────────────────────── */}
        <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 6, p: 'clamp(24px, 4vw, 48px)', borderRadius: '36px', bgcolor: '#FFFFFF' }}>
          <Typography component="h2" sx={h2}>How it works</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 1.5 }}>
            {HOW_IT_WORKS.map((s) => (
              <Box key={s.n} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: 2.75, borderRadius: '24px', bgcolor: 'var(--tl-mint)' }}>
                <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 15, color: 'var(--tl-teal-d)' }}>{s.n}</Box>
                <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 18, lineHeight: 1.25, color: 'var(--tl-g9)' }}>{s.t}</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.55, color: 'var(--tl-muted)' }}>{s.d}</Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── 7. FAQs ────────────────────────────────────────────────── */}
        <Box component="section" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 6 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25, p: 1 }}>
            <Box component="span" sx={eyebrow}>For HR teams</Box>
            <Typography component="h2" sx={h2}>Questions we&rsquo;re asked</Typography>
          </Box>
          <Box sx={{ flex: '2 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {CORPORATE_FAQS.map(([q, a], i) => {
              const open = faq === i;
              return (
                <Box key={q} sx={{ borderRadius: '20px', overflow: 'hidden', bgcolor: '#FFFFFF' }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setFaq(open ? -1 : i)}
                    aria-expanded={open}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      border: 0,
                      background: 'transparent',
                      p: '20px 22px',
                      font: 'inherit',
                      fontFamily: UI,
                      fontSize: 17,
                      fontWeight: 800,
                      color: 'var(--tl-g9)',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    {q}
                    <Box component="span" aria-hidden="true" sx={{ color: 'var(--tl-teal-d)' }}>{open ? '−' : '+'}</Box>
                  </Box>
                  {open && (
                    <Typography sx={{ m: 0, p: '0 22px 20px', fontFamily: UI, fontSize: 15, lineHeight: 1.65, color: 'var(--tl-muted)' }}>
                      {a}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ── 8. enquire ─────────────────────────────────────────────── */}
        <Box
          component="section"
          id="enquire"
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5, mt: 6, scrollMarginTop: '100px' }}
        >
          <Box sx={{ flex: '1 1 340px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.75, p: 1 }}>
            <Typography component="h2" sx={{ ...h2, fontSize: 'clamp(32px, 4vw, 48px)', lineHeight: 1.02 }}>
              Tell us about your team.
            </Typography>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 16, lineHeight: 1.65, color: 'var(--tl-muted)' }}>
              We&rsquo;ll send a proposal with packages, camp dates and per-employee pricing within
              one working day.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, fontFamily: UI, fontSize: 15, fontWeight: 700 }}>
              <Box component="a" href={`tel:${phone}`} sx={{ color: 'var(--tl-g9)', textDecoration: 'none' }}>{phoneLabel}</Box>
              <Box component="a" href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer" sx={{ color: 'var(--tl-g9)', textDecoration: 'none' }}>
                WhatsApp
              </Box>
              <Box component="a" href={`mailto:${email}`} sx={{ color: 'var(--tl-g9)', textDecoration: 'none' }}>{email}</Box>
            </Box>
          </Box>

          <Box sx={{ flex: '1.4 1 440px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.25, p: 'clamp(20px, 3.4vw, 32px)', borderRadius: '28px', bgcolor: '#FFFFFF' }}>
            {sent ? (
              <>
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: 'var(--tl-teal)', color: '#FFFFFF', display: 'grid', placeItems: 'center', fontSize: 24, fontWeight: 800 }}
                >
                  ✓
                </Box>
                <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 'clamp(26px, 3vw, 36px)', lineHeight: 1.05, letterSpacing: '-.02em', color: 'var(--tl-g9)' }}>
                  Thank you. We&rsquo;ll be in touch within a working day.
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 16, lineHeight: 1.65, color: 'var(--tl-muted)' }}>
                  A member of our corporate team will call you to understand your needs.
                </Box>
              </>
            ) : (
              <Box component="form" onSubmit={onSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1.25 }}>
                  <Box component="label" sx={fieldLabel}>
                    Company
                    <Box component="input" value={form.company} onChange={set('company')} placeholder="Company name" sx={field} />
                  </Box>
                  {/* Required by POST /corporate/enquiry, not in the reference. */}
                  <Box component="label" sx={fieldLabel}>
                    Industry
                    <Box component="input" value={form.industry} onChange={set('industry')} placeholder="e.g. IT services" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Your name
                    <Box component="input" value={form.contact_name} onChange={set('contact_name')} placeholder="Full name" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Designation
                    <Box component="input" value={form.designation} onChange={set('designation')} placeholder="e.g. HR Manager" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Work email
                    <Box component="input" value={form.email} onChange={set('email')} type="email" placeholder="name@company.com" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Phone
                    <Box component="input" value={form.phone} onChange={set('phone')} inputMode="tel" placeholder="+91" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Cities or sites
                    <Box component="input" value={form.locations} onChange={set('locations')} placeholder="e.g. Hyderabad, Vijayawada" sx={field} />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, color: 'var(--tl-muted-2)' }}>Team size</Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {TEAM_SIZES.map((l, i) => (
                      <Box key={l} component="button" type="button" onClick={() => setSize(i)} aria-pressed={i === size} sx={chipSx(i === size)}>
                        {l}
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, color: 'var(--tl-muted-2)' }}>What do you need? Pick any</Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {NEEDS.map((n) => (
                      <Box key={n.code} component="button" type="button" onClick={() => toggleNeed(n.code)} aria-pressed={needs.includes(n.code)} sx={chipSx(needs.includes(n.code))}>
                        {n.label}
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box component="label" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, cursor: 'pointer' }}>
                  <Box
                    component="input"
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    sx={{ width: 20, height: 20, mt: '2px', flexShrink: 0, accentColor: 'var(--tl-teal-d)', cursor: 'pointer' }}
                  />
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
                    I agree that TrustLab may contact me about this enquiry and store these
                    details.
                  </Box>
                </Box>

                {error && (
                  <Box component="span" role="alert" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: '#B23B37' }}>
                    {error}
                  </Box>
                )}

                <Box
                  component="button"
                  type="submit"
                  disabled={sending}
                  sx={{
                    alignSelf: 'start',
                    border: 0,
                    cursor: sending ? 'default' : 'pointer',
                    borderRadius: '16px',
                    p: '15px 22px',
                    font: 'inherit',
                    fontFamily: UI,
                    fontSize: 16,
                    fontWeight: 800,
                    bgcolor: sending ? '#B9C9C4' : 'var(--tl-g9)',
                    color: '#FFFFFF',
                  }}
                >
                  {sending ? 'Sending…' : 'Request a proposal'}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Corporate;
